import React, { FC, useMemo } from 'react';
import { nullSafeGet, nullSafeGetOrElse } from '../../utils/DataAccessUtils';
import ProblemTypeHierarchicalDisplay from '../problem_type_hierarchical_display/ProblemTypeHierarchicalDisplay';
import { Card } from 'antd';
import { TwoLineList } from '../two_line_list/TwoLineList';
import { connect } from 'react-redux';
import { ROLE_TYPES } from '../../utils/DataConstants';
import { isInternalTech } from '../../utils/AuthUtils';
import DateTimeHover from '../date_time_component/DateTime';
import { checkAndFormatPhoneNumber } from '../../utils/DataFormatterUtils';
import { AddressDisplay } from '../address_display/AddressDisplay';
import { isLocationBillToVisible } from '../../utils/LocationUtils';

const LocationDetailsCard: FC<any> = ({
	location,
	currentUser,
	companyConfig,
}): React.ReactElement => {
	const userType = useMemo(() => currentUser.userType, [currentUser.userType]);

	const isThirdPartySupplier = useMemo(
		() => userType === ROLE_TYPES.SUPPLIER && !isInternalTech(currentUser),
		[currentUser, userType]
	);

	const showBillTo = useMemo(
		() => isLocationBillToVisible(currentUser, companyConfig, location),
		[companyConfig, currentUser, location]
	);

	const locDetails = useMemo(
		() => [
			...(location.phoneNumber
				? [{ key: 'Location Phone', value: checkAndFormatPhoneNumber(location.phoneNumber) }]
				: []),
			...(nullSafeGet('region', location)
				? [{ key: 'Region', value: nullSafeGet('name', location.region) }]
				: []),
			...(!isThirdPartySupplier && nullSafeGet('buyerFacility.currencyId', location)
				? [{ key: 'Currency', value: nullSafeGet('buyerFacility.currencyId', location) }]
				: []),
			...(!isThirdPartySupplier && nullSafeGet('isLeased', location)
				? [{ key: 'Lease Type', value: 'Leased' }]
				: []),
			...(!isThirdPartySupplier && nullSafeGet('leaseStartDate', location)
				? [
						{
							key: 'Lease Start Date',
							value: (
								<DateTimeHover
									timestamp={nullSafeGet('leaseStartDate', location)}
									showDate={true}
									showTime={false}
									timeZone={nullSafeGet('timeZoneId', location)}
								/>
							),
						},
				  ]
				: []),
			...(!isThirdPartySupplier && nullSafeGet('leaseEndDate', location)
				? [
						{
							key: 'Lease End Date',
							value: (
								<DateTimeHover
									timestamp={nullSafeGet('leaseEndDate', location)}
									showDate={true}
									showTime={false}
									timeZone={nullSafeGet('timeZoneId', location)}
								/>
							),
						},
				  ]
				: []),
			...(!isThirdPartySupplier && nullSafeGet('openDate', location)
				? [
						{
							key: 'Open Date',
							value: (
								<DateTimeHover
									timestamp={nullSafeGet('openDate', location)}
									showDate={true}
									showTime={false}
									timeZone={nullSafeGet('timeZoneId', location)}
								/>
							),
						},
				  ]
				: []),
			...(!isThirdPartySupplier && nullSafeGet('closeDate', location)
				? [
						{
							key: 'Close Date',
							value: (
								<DateTimeHover
									timestamp={nullSafeGet('closeDate', location)}
									showDate={true}
									showTime={false}
									timeZone={nullSafeGet('timeZoneId', location)}
								/>
							),
						},
				  ]
				: []),
			...nullSafeGetOrElse('locationTypeDetails', location, []),
			...(nullSafeGet('buildingWarrantyStartDate', location)
				? [
						{
							key: 'Building Warranty Start Date',
							value: (
								<DateTimeHover
									timestamp={nullSafeGet('buildingWarrantyStartDate', location)}
									showDate={true}
									showTime={false}
									timeZone={nullSafeGet('timeZoneId', location)}
								/>
							),
						},
				  ]
				: []),
			...(nullSafeGet('buildingWarrantyExpirationDate', location)
				? [
						{
							key: 'Building Warranty End Date',
							value: (
								<DateTimeHover
									timestamp={nullSafeGet('buildingWarrantyExpirationDate', location)}
									showDate={true}
									showTime={false}
									timeZone={nullSafeGet('timeZoneId', location)}
								/>
							),
						},
				  ]
				: []),
			...(nullSafeGet('buildingWarrantyProblemTypes.0', location)
				? [
						{
							key: 'Building Warranty Problem Types',
							value: (
								<ProblemTypeHierarchicalDisplay
									problemTypeIds={nullSafeGetOrElse(
										'buildingWarrantyProblemTypes',
										location,
										[]
									).map((_) => _.id)}
								/>
							),
						},
				  ]
				: []),
			...(nullSafeGet('buildingWarrantyServiceProvider.name', location)
				? [
						{
							key: 'Building Warranty Service Provider',
							value: nullSafeGet('buildingWarrantyServiceProvider.name', location),
						},
				  ]
				: []),
			...(showBillTo && nullSafeGet('invoicesBillTo.billTo', location)
				? [
						{
							key: 'Invoice Bill To',
							value: nullSafeGet('invoicesBillTo.billTo', location),
						},
				  ]
				: []),
			...(showBillTo && nullSafeGet('invoicesBillTo', location)
				? [
						{
							key: 'Invoice Service Address',
							value: (
								<AddressDisplay
									address={
										nullSafeGet('invoicesBillTo.serviceAddress.sameAsLocation', location)
											? nullSafeGet('buyerFacility.primaryAddress', location)
											: nullSafeGet('invoicesBillTo.serviceAddress', location)
									}
								/>
							),
						},
				  ]
				: []),
		],
		[isThirdPartySupplier, location, showBillTo]
	);

	return locDetails.length > 0 ? (
		<div className="rowSpacing">
			<Card title="Location Details" className="materialCard" bodyStyle={{ padding: 0 }}>
				<div className="materialCard__body">
					<TwoLineList
						listItems={locDetails}
						lineOneRender={(record) => record.value || record.valueAsString || '--'}
						lineTwoRender={(record) => record.key}
					/>
				</div>
			</Card>
		</div>
	) : null;
};

const mapStateToProps = (state) => ({
	currentUser: state.session.currentUser,
	companyConfig: state.company_config.detail,
});

export default connect(mapStateToProps)(LocationDetailsCard);
